import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { klubbleAPI } from 'client/api/common'
import { SubscriptionType } from 'client/enums/SubscriptionType'
import { PaymentPeriod } from 'client/pages/payment_input/PaymentStepsComponent'
import { useAsyncError } from './useAsyncError'

export type Currency = 'EUR' | 'SEK'

export type PriceModelState = {
  paymentPeriodId: PaymentPeriod
  amount: number,
  currency: Currency,
  discountPercentage: number
}

export const usePriceModels = () => {
  const abortController = useRef(null)

  const [priceModels, setPriceModels] = useState<PriceModelState[]>([])
  const throwError = useAsyncError()

  useEffect(() => {
    return () => {
      abortController.current?.abort()
    }
  }, [])

  const getAllPriceModels = useCallback(async () => {
    if (!priceModels.length) {
      try {
        abortController.current = new AbortController()
        const { data } = await klubbleAPI.get<PriceModelState[]>('Auth/getavailablepricemodels', { signal: abortController.current.signal })
        setPriceModels(data)
        return data
      } catch (e) {
        throwError(e, 'Error getting available price models')
      }
    }
    return priceModels
  }, [priceModels, throwError])

  const getPriceModel = useCallback(async (subscriptionType: SubscriptionType, paymentPeriodId: PaymentPeriod) => {
    if (subscriptionType !== SubscriptionType.premium) {
      return {}
    }

    let priceModel

    if (!priceModels?.length) {
      const data = await getAllPriceModels()
      priceModel = data.find((p) => p.paymentPeriodId === paymentPeriodId)
    } else {
      priceModel = priceModels.find((p) => p.paymentPeriodId === paymentPeriodId)
    }

    return {
      amount: priceModel?.amount,
      currency: priceModel?.currency,
      discountPercentage: priceModel?.discountPercentage,
    }
  }, [getAllPriceModels, priceModels])

  return {
    getAllPriceModels,
    getPriceModel,
  }
}
